/**
 * Terrain-window OBJECTS-tool authoring model — the pure, testable heart of placing/deleting
 * destructible objects from the TERRAIN window, mirroring the split `combatAuthoring.ts`
 * (terrain painting) and `deploy.ts` (unit deploy) already use: the {@link CombatStage} owns
 * pointer wiring + dispatch; everything *decided* lives here so it is unit-tested without a
 * canvas.
 *
 * **Command-first, the SAME path the engine uses**: a placement is built from the same
 * `spawnObjectGroup` (`@lancer-vtt/rules`) the CLI/engine path uses, then issued as the ordinary
 * `add_combatant` + `place_token` commands — never a hand-built `Combatant` or a bespoke spatial
 * mutation. `place_token` (not a raw `pos` on `add_combatant`) is what gets the reducer's
 * structural invariants (grid match, footprint fit, non-overlap) for free — the whole
 * multi-section placement is issued as ONE atomic `batch`, so a wall's sections land — or a
 * blocked/off-map click rejects — as a single unit (one undo entry, no partial spawn: `batch`
 * is atomic, `shared/src/index.ts`'s `applyWithReducer`).
 *
 * **Sections lay out along one axis from the clicked anchor** ({@link objectLineCells}): the
 * simplest placement gesture that still expresses "an authored SIZE-n object is n adjacent
 * SIZE-1 sections" without a second drag-vs-click gesture grammar alongside the tile/wall/room
 * tools this window already has.
 *
 * **The preset catalog is UI convenience, not a new model concept** — every preset just
 * pre-fills `objectProfile` (`CombatantBase.objectProfile`) with RAW-legible defaults (Core
 * p.87's wall/boulder/smoke shorthand); armor and section count stay separately authorable.
 *
 * **Map-asset presets** (`map-assets` Phase 4, `decisions/0193`): the `MAP_ASSETS` catalogue is offered
 * beside the plain presets. An asset pick places through `spawnAsset`, the builder `lvtt encounter add
 * --asset` calls, so the browser and the CLI dispatch the identical batch; its mechanics come from the
 * catalogue, so only a sectioned asset's section count and axis are authorable.
 *
 * **The preset gap:** objects (combatants) don't ride scene/map presets — only terrain/edges/
 * rooms do. The rail's Objects panel (`BuildTools.tsx`) says so in its hint copy; this module carries no
 * preset-save/-load logic to gap-fill.
 */
import {
  MAP_ASSET_IDS,
  isMapAssetId,
  mapAsset,
  objectGroupMembers,
  squareCell,
  type Combatant,
  type CombatantKind,
  type EncounterCommand,
  type EncounterState,
  type MapAssetId,
  type ObjectProfile,
  type SquareCell,
} from "@lancer-vtt/domain";
import { spawnAsset, spawnObjectGroup } from "@lancer-vtt/rules";
import type { WallHeight } from "./combatAuthoring";
import { ok, type Result } from "@lancer-vtt/shared";

/** Mint an id for a freshly authored object group (same minting discipline as `deploy.ts`'s `deployId`). */
export function objectGroupId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `obj-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/** The OBJECTS tool's preset picker. */
export type ObjectPreset = "wall" | "boulder" | "smoke" | "door";

/** One preset's defaults: display name, `kind`, `objectProfile`, and the section-count/armor a
 *  fresh pick pre-fills the steppers to — the GM can still change either afterward. */
export type ObjectPresetDef = {
  label: string;
  kind: Extract<CombatantKind, "object" | "deployable">;
  objectProfile: ObjectProfile;
  /** The section count a fresh pick of this preset defaults to (the GM can still change it). */
  defaultSectionCount: number;
  /** The armor a fresh pick of this preset defaults to (the GM can still change it). */
  defaultArmor: number;
  /**
   * The profile this preset places at LOW height. Present only on a preset with a height choice
   * (Wall); {@link objectProfile} is then its full-height profile.
   */
  lowObjectProfile?: ObjectProfile;
};

/**
 * The presets. **wall** — the classic multi-section obstacle (defaults to 3 sections), with a
 * height choice mirroring the wall tool's (`flight-relevant-terrain` D4): **full** (the default)
 * blocks movement, sight AND flight with hard cover, a floor-to-ceiling wall a flyer can't pass
 * over; **low** blocks ground movement only, sight passes, and grants soft cover (RAW p.66 lists
 * low walls as soft cover), so a flyer clears it. **boulder** — blocks movement and sight with hard
 * cover, a single lump (1 section), and flyable: the RAW-default outdoor obstacle, like the solid
 * cell brush. **smoke** — `cover:
 * "soft"` only (doesn't block movement or sight, per Core's smoke-screen shorthand, and a placed
 * Smoke is genuinely walk-through-and-endable); modeled `kind: "deployable"` since it's the
 * Jericho-style deployable-cover shape, but `deployable` is behaviorally identical to `object`
 * here — only the kind tag differs, with no owner-linkage/pick-up/repair extras. **door** — a
 * shut-wall profile (blocks movement and sight, hard cover; NOT full-height, so a flyer can pass
 * over a shut door) plus `door: true` (what the marker `isOpenDoor`/
 * `set_door_state` gate on), authored SHUT (a fresh spawn carries no status —
 * `spawnObjectGroup` always starts `statuses: []`, so "shut" is simply the absence of the "Open"
 * marker), defaulting to a single SIZE-1 doorway section and **armor 2** ("tough material" —
 * Core p.87's reinforced-bulkhead 3-4 Armor is the *fortified* example; the GM can step it up
 * via the Armor stepper same as any other preset).
 */
export const OBJECT_PRESETS: Record<ObjectPreset, ObjectPresetDef> = {
  wall: {
    label: "Wall",
    kind: "object",
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", blocksFlight: true },
    lowObjectProfile: { blocksMovement: true, cover: "soft" },
    defaultSectionCount: 3,
    defaultArmor: 0,
  },
  boulder: {
    label: "Boulder",
    kind: "object",
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard" },
    defaultSectionCount: 1,
    defaultArmor: 0,
  },
  smoke: {
    label: "Smoke",
    kind: "deployable",
    objectProfile: { cover: "soft" },
    defaultSectionCount: 1,
    defaultArmor: 0,
  },
  door: {
    label: "Door",
    kind: "object",
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", door: true },
    defaultSectionCount: 1,
    defaultArmor: 2,
  },
};

/** A pick in the OBJECTS panel: a plain preset, or a `MAP_ASSETS` entry by its asset id. */
export type ObjectPick = ObjectPreset | MapAssetId;

/** The map-asset presets the panel lists: the whole catalogue, in catalogue order. */
export const ASSET_PRESET_LIST: readonly MapAssetId[] = MAP_ASSET_IDS;

/** Whether `preset` offers the full | low height choice (only Wall does). */
export function presetHasHeight(preset: ObjectPick): boolean {
  return !isMapAssetId(preset) && OBJECT_PRESETS[preset].lowObjectProfile !== undefined;
}

/** A pick's display name: the preset label, or the asset's catalogue name. */
export function pickLabel(pick: ObjectPick): string {
  return isMapAssetId(pick) ? mapAsset(pick)!.name : OBJECT_PRESETS[pick].label;
}

/** The Sections stepper's range for `pick`, or `null` for an asset with one fixed footprint. */
export function pickSectionRange(pick: ObjectPick): { min: number; max: number } | null {
  if (!isMapAssetId(pick)) return { min: MIN_SECTIONS, max: MAX_SECTIONS };
  return mapAsset(pick)!.sections ?? null;
}

/** What a fresh pick pre-fills the steppers to. An asset's armor is its catalogue value, not authorable. */
export function pickDefaults(pick: ObjectPick): { sectionCount: number; armor: number } {
  if (!isMapAssetId(pick)) return { sectionCount: OBJECT_PRESETS[pick].defaultSectionCount, armor: OBJECT_PRESETS[pick].defaultArmor };
  const asset = mapAsset(pick)!;
  return { sectionCount: asset.sections?.min ?? 1, armor: asset.armor };
}

/** The `objectProfile` a placement of `preset` at `height` spawns with. `height` only matters for a preset with a height choice. */
export function presetObjectProfile(preset: ObjectPreset, height: WallHeight = "full"): ObjectProfile {
  const def = OBJECT_PRESETS[preset];
  return height === "low" && def.lowObjectProfile ? def.lowObjectProfile : def.objectProfile;
}

/** The ordered preset list the rail's picker renders (object insertion order — wall/boulder/smoke/door). */
export const OBJECT_PRESET_LIST: ObjectPreset[] = ["wall", "boulder", "smoke", "door"];

/** The lowest/highest section count the SIZE stepper allows (1 = a monolithic single section). */
export const MIN_SECTIONS = 1;
export const MAX_SECTIONS = 8;

/** The lowest/highest armor the Armor stepper allows (Core p.87: 0 unarmored, 1-2 tough, 3-4 fortified). */
export const MIN_ARMOR = 0;
export const MAX_ARMOR = 4;

/** Which axis a multi-section object's sections extend along from the clicked anchor cell. */
export type ObjectOrientation = "horizontal" | "vertical";

/**
 * The `count` adjacent cells a multi-section object's sections occupy, extending from `anchor`
 * along `orientation` (horizontal → increasing `x`, vertical → increasing `y`). Pure geometry —
 * `place_token` (not this) is what validates on-map/non-overlap; a click near the board edge
 * simply rejects at the reducer, same as any other authoring placement.
 */
export function objectLineCells(anchor: SquareCell, orientation: ObjectOrientation, count: number): SquareCell[] {
  const n = Math.max(1, Math.floor(count));
  const cells: SquareCell[] = [];
  for (let i = 0; i < n; i++) {
    cells.push(orientation === "horizontal" ? squareCell(anchor.x + i, anchor.y) : squareCell(anchor.x, anchor.y + i));
  }
  return cells;
}

/**
 * The OBJECTS panel's currently-configured pick — preset + SIZE/sections + armor + orientation (+ height for a Wall)
 * — lifted in `CombatApp` alongside the other client authoring state (`brush`/`coverGrade`/…)
 * and threaded into {@link CombatStage} so a board click has something to place. `null` means no
 * preset picked yet (the panel defaults one in as soon as the OBJECTS tool arms, but the type
 * stays nullable so the stage can no-op before that first render lands).
 */
export type ObjectAuthoringPlan = {
  preset: ObjectPick;
  /** How many SIZE-1 sections to author (a fresh Wall pick defaults to 3; 1 = a monolithic single section). */
  sectionCount: number;
  /** Tough/fortified material (Core p.87: 1-2 tough, 3-4 fortified); 0 = unarmored. Ignored for an asset. */
  armor: number;
  /** Which axis the sections extend along from the clicked anchor — irrelevant when `sectionCount === 1`. */
  orientation: ObjectOrientation;
  /**
   * The Wall preset's height (full | low), the object twin of the wall tool's toggle. Absent means
   * `"full"`. Ignored by a preset without a height choice ({@link presetHasHeight}).
   */
  height?: WallHeight;
};

/** Named arguments for {@link objectPlacementCommand}. */
export type ObjectPlacementArgs = ObjectAuthoringPlan & {
  actorId: string;
  /** The clicked board cell — the first section's cell (and the only one when `sectionCount === 1`). */
  anchor: SquareCell;
  /** Override the minted group id (tests pass a deterministic one). */
  groupId?: string;
};

/**
 * Build the ONE atomic `batch` that spawns + places a preset object: mints
 * `sectionCount` SIZE-1 sections sharing one `objectGroupId` via the SAME `spawnObjectGroup`
 * the engine/CLI path uses, then pairs each section with an `add_combatant` (unplaced) +
 * `place_token` (footprint-validated placement at its line cell) — `place_token`, not a raw
 * `pos`, is what threads the reducer's structural invariants (grid match/footprint/overlap)
 * through this authoring surface (the command-first convention: the UI never re-enforces what
 * the reducer already gates). A rejection (an off-map/overlapping cell for ANY section) fails
 * the whole batch atomically — nothing spawns, mirroring `place_token`'s own no-partial-spawn
 * guarantee for a single unit. Propagates `spawnObjectGroup`'s own rejection (a structurally
 * invalid `sectionCount`/armor) verbatim — the UI's own steppers keep both in range, so this
 * should never actually fire outside a test. A map-asset pick builds {@link spawnAsset}'s batch instead.
 */
export function objectPlacementCommand(args: ObjectPlacementArgs): Result<EncounterCommand, string> {
  const { actorId, preset, sectionCount, armor, orientation, anchor, height, groupId = objectGroupId() } = args;
  if (isMapAssetId(preset)) {
    // The CLI's `add --asset` call, so the two can't disagree. `sections` goes only to a sectioned asset: `spawnAsset` refuses it on any other.
    const sectioned = mapAsset(preset)!.sections !== undefined;
    const spawned = spawnAsset({ actorId, assetId: preset, at: anchor, id: groupId, axis: orientation === "horizontal" ? "h" : "v", ...(sectioned ? { sections: sectionCount } : {}) });
    return spawned.ok ? ok(spawned.value.command) : spawned;
  }
  const def = OBJECT_PRESETS[preset];
  const cells = objectLineCells(anchor, orientation, sectionCount);
  const spawned = spawnObjectGroup({
    groupId,
    name: def.label,
    sectionCount: cells.length,
    kind: def.kind,
    armor,
    objectProfile: presetObjectProfile(preset, height),
  });
  if (!spawned.ok) return spawned;
  const commands: EncounterCommand[] = [];
  spawned.value.forEach((section, i) => {
    commands.push({ type: "add_combatant", actorId, combatant: section });
    commands.push({ type: "place_token", actorId, combatantId: section.id, to: cells[i]! });
  });
  return ok({ type: "batch", commands });
}

/** One placed object group, as the OBJECTS panel's delete list reads it. */
export type ObjectGroupSummary = {
  /** The `objectGroupId`, or a lone asset's combatant id. */
  groupId: string;
  /** The group's display name — the shared preset label, sections' `" (i/N)"` suffix stripped. */
  name: string;
  kind: CombatantKind;
  memberIds: string[];
};

const SECTION_SUFFIX = / \(\d+\/\d+\)$/;

/**
 * Every currently-placed object group (the panel's group-delete list) — every distinct
 * `objectGroupId` among `state.combatants`, plus each lone map asset (a tree is one combatant with
 * no group) as a one-member entry, sorted by id for a stable list order.
 * `objectGroupMembers` (`@lancer-vtt/domain`) is the per-group read this folds over one group id
 * at a time; this is the "what groups exist at all" scan that read doesn't offer.
 */
export function listObjectGroups(state: EncounterState): ObjectGroupSummary[] {
  const groupIds = new Set<string>();
  const lone: Combatant[] = [];
  for (const c of Object.values(state.combatants)) {
    if (c.objectGroupId) groupIds.add(c.objectGroupId);
    else if (c.assetId !== undefined) lone.push(c);
  }
  return [
    ...[...groupIds].map((groupId) => summarize(groupId, objectGroupMembers(state, groupId))),
    ...lone.map((c) => summarize(c.id, [c])),
  ].sort((a, b) => (a.groupId < b.groupId ? -1 : a.groupId > b.groupId ? 1 : 0));
}

/** The delete for one listed entry: a `remove_combatant` per member, batched when there are several. */
export function placedObjectRemoveCommand(actorId: string, entry: ObjectGroupSummary): EncounterCommand {
  const removes: EncounterCommand[] = entry.memberIds.map((combatantId) => ({ type: "remove_combatant", actorId, combatantId }));
  return removes.length === 1 ? removes[0]! : { type: "batch", commands: removes };
}

function summarize(groupId: string, members: Combatant[]): ObjectGroupSummary {
  const first = members[0]!;
  return {
    groupId,
    name: first.name.replace(SECTION_SUFFIX, ""),
    kind: first.kind,
    memberIds: members.map((m) => m.id),
  };
}
